"use client"

import type { ReactNode } from "react"
import { ArrowRightLeft, Clock, UserCheck, UserPlus, Users, Zap, type LucideIcon } from "lucide-react"

import { KpiCard, KpiCardSkeleton } from "@/components/ui/analytics/kpi-card"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { MetricKind } from "@/lib/analytics/deltas"
import { formatShare } from "@/lib/analytics/format"
import {
  ACTOR_LABELS,
  AI_HANDLED_POPULATION,
  ATTRIBUTION_CAPTIONS,
  COHORT_LABELS,
  NO_VALUE,
  SERVICE_METRIC_DEFINITIONS,
  SERVICE_METRIC_POPULATION,
  share,
  type ServiceMetricKey,
} from "@/lib/conversation-quality/metric-definitions"
import type { ServiceMetricsResult } from "@/server-actions/conversation-quality"

import { formatRatio, leader, MetricFaceOff, PairedBars } from "./metric-comparison"
import { DefinedTerm, duration, EmptyNote, SectionCard, sessionCount, shareComparison } from "./section-parts"

const percent = (value: number | null) => formatShare(value, NO_VALUE)

type Fate = ServiceMetricsResult["current"]["escalations"]

const HANDOVER_TILES_GRID = "grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5"

/** Share of one fate over its population, now against before, compared where a window exists. */
function RateTile({
  data,
  metric,
  icon,
  of,
  over,
  kind,
  subtitle,
}: {
  data: ServiceMetricsResult;
  metric: ServiceMetricKey;
  icon: LucideIcon;
  of: (fate: Fate) => number;
  over: (fate: Fate) => number;
  /** Which direction reads as good — a transfer is an expense, a pick-up a success. */
  kind: MetricKind;
  subtitle: ReactNode;
}) {
  const now = data.current.escalations
  const before = data.previous.escalations
  const current = share(of(now), over(now))

  return (
    <KpiCard
      label={SERVICE_METRIC_DEFINITIONS[metric].label}
      tooltip={SERVICE_METRIC_DEFINITIONS[metric].definition}
      icon={icon}
      formattedValue={percent(current)}
      {...shareComparison(current, data.hasComparison ? { of: of(before), over: over(before) } : null, kind)}
      subtitle={subtitle}
    />
  )
}

function answered(fate: Fate): number {
  return fate.pickedUp + fate.answeredByEmail
}

/**
 * Where the team replied; email is named only where the mailbox answered any, with what that count can claim. One
 * element either way, its text whole, so a translated page can switch between the two.
 */
function Replies({ fate: { pickedUp, answeredByEmail } }: { fate: Fate }) {
  if (answeredByEmail === 0) return <span>{`a person replied in ${pickedUp.toLocaleString()}`}</span>

  const { label, definition } = SERVICE_METRIC_DEFINITIONS.answeredByEmail
  return (
    <span>
      <span>{`replied in chat ${pickedUp.toLocaleString()} · `}</span>
      <DefinedTerm label={`${label} ${answeredByEmail.toLocaleString()}`} definition={definition} />
    </span>
  )
}

/** How often, how many were answered, how fast, and how many times the team came unasked. */
export function HandoverTiles({ data }: { data: ServiceMetricsResult | undefined }) {
  if (!data) {
    return (
      <div className={HANDOVER_TILES_GRID}>
        {Array.from({ length: 5 }, (_, i) => (
          <KpiCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  const now = data.current.escalations
  return (
    <div className={HANDOVER_TILES_GRID}>
      <RateTile
        data={data}
        metric="transferRate"
        icon={UserPlus}
        of={(fate) => fate.transferred}
        over={(fate) => fate.aiHandledSessions}
        kind="negative"
        subtitle={`${now.transferred.toLocaleString()} of ${now.aiHandledSessions.toLocaleString()} ${AI_HANDLED_POPULATION}`}
      />
      <RateTile
        data={data}
        metric="escalationRate"
        icon={ArrowRightLeft}
        of={(fate) => fate.escalated}
        over={(fate) => fate.aiHandledSessions}
        kind="negative"
        subtitle={`${now.escalated.toLocaleString()} of ${now.aiHandledSessions.toLocaleString()} ${AI_HANDLED_POPULATION}`}
      />
      <RateTile
        data={data}
        metric="pickedUp"
        icon={UserCheck}
        of={answered}
        over={(fate) => fate.escalatedSessions}
        kind="positive"
        subtitle={
          <>
            <Replies fate={now} />
            <span>{` of ${now.escalatedSessions.toLocaleString()}`}</span>
          </>
        }
      />
      <KpiCard
        label={SERVICE_METRIC_DEFINITIONS.timeToHuman.label}
        tooltip={SERVICE_METRIC_DEFINITIONS.timeToHuman.definition}
        icon={Clock}
        formattedValue={duration(now.timeToHumanMedianMs)}
        subtitle={
          now.timeToHumanMedianMs === null
            ? "no escalation was answered in the conversation"
            : `over ${now.pickedUp.toLocaleString()} answered in the conversation`
        }
      />
      <KpiCard
        label={SERVICE_METRIC_DEFINITIONS.operatorTakeovers.label}
        tooltip={SERVICE_METRIC_DEFINITIONS.operatorTakeovers.definition}
        icon={Users}
        formattedValue={now.operatorTakeovers.toLocaleString()}
        subtitle="a person stepped in unasked"
      />
    </div>
  )
}

/** The two halves carry different captions because they are attributed differently. */
export function ServiceMetrics({ data }: { data: ServiceMetricsResult | undefined }) {
  return (
    <SectionCard
      title="Service metrics"
      subtitle={`${SERVICE_METRIC_POPULATION}${data ? ` — ${data.current.escalations.sessions.toLocaleString()} in this period` : ""}`}
    >
      {!data ? (
        <Skeleton className="h-56 w-full" />
      ) : (
        <div className="space-y-4">
          <FirstAnswerHeadline data={data} />
          <div className="grid gap-6 lg:grid-cols-2 lg:gap-10">
            <ComparisonGroup
              caption={ATTRIBUTION_CAPTIONS.phase}
              footer={`${data.current.messages.ai.toLocaleString()} AI messages · ${data.current.messages.team.toLocaleString()} team messages`}
            >
              <MetricFaceOff
                metric="firstResponse"
                leftLabel={ACTOR_LABELS.ai}
                rightLabel={ACTOR_LABELS.team}
                left={data.current.ai.firstMedianMs}
                right={data.current.team.firstMedianMs}
              />
              <MetricFaceOff
                metric="replyTime"
                leftLabel={ACTOR_LABELS.ai}
                rightLabel={ACTOR_LABELS.team}
                left={data.current.ai.replyMedianMs}
                right={data.current.team.replyMedianMs}
              />
            </ComparisonGroup>
            <ComparisonGroup
              caption={ATTRIBUTION_CAPTIONS.cohort}
              footer={`${sessionCount(data.current.aiOnly.sessions)} the agent handled alone · ${sessionCount(data.current.teamInvolved.sessions)} your team took part in`}
            >
              <MetricFaceOff
                metric="handleTime"
                leftLabel={COHORT_LABELS.aiOnly}
                rightLabel={COHORT_LABELS.teamInvolved}
                left={data.current.aiOnly.handleTimeMedianMs}
                right={data.current.teamInvolved.handleTimeMedianMs}
              />
              <PairedBars
                metric="customerEffort"
                leftLabel={COHORT_LABELS.aiOnly}
                rightLabel={COHORT_LABELS.teamInvolved}
                left={data.current.aiOnly.customerMessagesAvg}
                right={data.current.teamInvolved.customerMessagesAvg}
                format={formatEffort}
              />
            </ComparisonGroup>
          </div>
        </div>
      )}
    </SectionCard>
  )
}

/** The period's loudest reading, said once: how soon a customer hears back is the first thing anyone asks. */
function FirstAnswerHeadline({ data }: { data: ServiceMetricsResult }) {
  const ahead = leader([
    { name: ACTOR_LABELS.ai, value: data.current.ai.firstMedianMs },
    { name: ACTOR_LABELS.team, value: data.current.team.firstMedianMs },
  ])
  if (!ahead) return null

  const behind = ahead.name === ACTOR_LABELS.ai ? ACTOR_LABELS.team : ACTOR_LABELS.ai
  return (
    <p className="flex items-center gap-2 rounded-md bg-green-600/10 px-3 py-2 text-sm font-medium text-green-600 dark:text-green-400">
      <Zap className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{`${ahead.name} answers first ${formatRatio(ahead.ratio)} faster than ${midSentence(behind)}`}</span>
    </p>
  )
}

/** «Your team» opens a sentence, not the middle of one. */
function midSentence(label: string): string {
  return label.replace(/^Your /, "your ")
}

/** A caption naming how its metrics are attributed, and a footer counting what they were measured over. */
function ComparisonGroup({
  caption,
  footer,
  children,
}: {
  caption: string;
  footer: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">{caption}</p>
      {children}
      <p className="text-xs text-muted-foreground">{footer}</p>
    </div>
  )
}

export function TeamTable({ data }: { data: ServiceMetricsResult | undefined }) {
  return (
    <SectionCard title="Team" subtitle="who answered, and how fast">
      {!data ? (
        <Skeleton className="h-24 w-full" />
      ) : data.operators.length === 0 ? (
        <EmptyNote text="No sessions a person took part in." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-0">Operator</TableHead>
              <TableHead className="text-right">Sessions</TableHead>
              <TableHead className="text-right">
                {SERVICE_METRIC_DEFINITIONS.firstResponse.label}
              </TableHead>
              <TableHead className="text-right">{SERVICE_METRIC_DEFINITIONS.replyTime.label}</TableHead>
              <TableHead className="pr-0 text-right">
                {SERVICE_METRIC_DEFINITIONS.escalationsHandled.label}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.operators.map((operator) => (
              <TableRow key={`${operator.operatorId ?? ""}|${operator.operatorName ?? ""}`}>
                <TableCell className="pl-0">
                  {operator.operatorName ?? (
                    <div className="min-w-0">
                      <p>Replied outside the inbox</p>
                      <p className="text-xs text-muted-foreground">
                        In the channel&apos;s own app or a CRM, so no team member is named
                      </p>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">{operator.sessions}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {duration(operator.firstResponseMedianMs)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {duration(operator.replyMedianMs)}
                </TableCell>
                <TableCell className="pr-0 text-right tabular-nums">
                  {operator.escalationsPickedUp}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  )
}

function formatEffort(value: number): string {
  return value.toFixed(1)
}
